import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCw } from 'lucide-react';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();

/** One-time backfill: turn every past-32-week / delivered customer into a
 *  renewal lead. Safe to re-run — existing rows keep their status and owner. */
const RenewalBackfillCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/renewals/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchStats(); }, []);

  const run = async () => {
    if (!window.confirm('Create renewal leads for every existing customer past her 32nd week or delivered?\n\nPostnatal payers are auto-marked Renewed. No emails are sent for historical leads.')) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/renewals/backfill`, {}, { withCredentials: true });
      toast.success(`${data.stats.created} renewal lead(s) created · ${data.stats.auto_renewed} marked Renewed`);
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card mt-6" data-testid="renewal-backfill-card">
      <div className="card-body">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Renewal Leads — One-time Backfill</h3>
            <p className="text-sm text-slate-500 mt-1">
              Creates a postnatal renewal lead for every existing customer who has already touched her
              32nd week or delivered, assigns it to the rep who collected her first Elite payment, and
              marks anyone who already bought postnatal as <b>Renewed</b>.
            </p>
          </div>
          <button onClick={fetchStats} disabled={loading}
            className="text-xs text-indigo-600 hover:text-indigo-700 whitespace-nowrap"
            data-testid="renewal-backfill-refresh">
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="renewal-backfill-stats">
            {[['Renewal leads', stats.total], ['Renewed', stats.renewed], ['Postnatal pitch', stats.postnatal_cycle]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 bg-white p-2.5">
                <div className="text-[11px] text-slate-500">{l}</div>
                <div className="text-lg font-bold text-slate-900">{v ?? 0}</div>
              </div>
            ))}
          </div>
        )}

        <button onClick={run} disabled={running}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-400 text-sm font-medium"
          data-testid="renewal-backfill-run">
          <RotateCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Running…' : 'Run backfill now'}
        </button>
      </div>
    </div>
  );
};

export default RenewalBackfillCard;
